// Everything that points the camera at something, and the one thing that tells
// it what the interface is covering.
//
// **The drivers ask through `Events.focus`, and this decides.** A `look` is
// the engine borrowing the view — for a move, an event, or new ground — and
// the player's toggles are read here, on the client, where they live: the
// engine says what is worth seeing, and whether the camera goes is the
// player's. `return` hands the view back afterwards.

import { useCallback, useEffect, useRef, type RefObject } from "react";

import type { Game } from "src/api";
import { boundsEqual, CLASSIC_BOUNDS, type Side } from "src/engine/rules/board";
import type { GameState } from "src/engine/rules/commands";
import type { BoardCanvasHandle } from "./BoardCanvas";
import type { CameraPrefs } from "./cameraPrefs";
import { useEdgeInsets } from "./kit";

export interface CameraLooks {
  /** What the board is managing, for the driver's beat. */
  clockHealth: () => number;
  /** The classic eight-by-eight, which refuses every borrowed look. */
  standardBoard: boolean;
}

export function useCameraLooks(
  game: Game,
  board: RefObject<BoardCanvasHandle | null>,
  state: GameState,
  /**
   * Whether anything but a drag may move the view, and how far that goes.
   * Read through a ref, so the subscription below is made once.
   */
  camera: CameraPrefs,
  /** Which side the board faces. See `GameShell`'s `viewpoint`. */
  viewpoint: Side
): CameraLooks {
  const cameraRef = useRef(camera);
  cameraRef.current = camera;

  /**
   * A standard board never gets a camera offset, whatever the toggles say.
   *
   * The whole justification for a borrowed look is ground the player cannot
   * see. On the classic eight-by-eight there is none — the board is on screen
   * in its entirety — so leading to a move is a shove, and handing the view
   * back afterwards is a second one. That is a fact about the board rather
   * than a preference, which is why it is here and not a third checkbox.
   */
  const standardBoard = boundsEqual(state.position.bounds, CLASSIC_BOUNDS);
  const standardRef = useRef(standardBoard);
  standardRef.current = standardBoard;

  /**
   * Hand the camera to whoever is on the clock — but only in hot-seat.
   *
   * Two people sharing a screen want the board to turn to face them, and each
   * to keep the angle they set. Two people on their own screens do not: your
   * view spinning away every time your opponent's turn began would be
   * disorienting and would move the board while you were mid-thought. So a
   * seated client pins the camera to its own seat and leaves it there.
   */
  // Single player pins it too, for the same reason a seated client does: the
  // opponent is a bot, so handing it the board on its turn spins the view away
  // to show a viewpoint nobody is sitting at.
  useEffect(() => {
    board.current?.focusSide(viewpoint);
  }, [board, viewpoint]);

  /**
   * How far in the floating chrome reaches, from the inset registry.
   *
   * **The registry is the whole answer now** (PLAN-UI §28.1, §28.6). The dock
   * died at U8 and with it the last thing whose geometry the shell had to know:
   * every floating piece measures itself and registers the rect it occupies —
   * the readouts and the turn row on `top`, the hotbar `left`, the tool rail
   * `right`, the hand `bottom`, the dev panel `left` while it is open — and the
   * inspector and the objectives with `edge: null`, so selecting something or
   * finishing an objective moves nothing.
   */
  const insets = useEdgeInsets();

  /**
   * Tell the camera what the interface is covering, so the board centres in
   * the part of the page that is left rather than in the window.
   *
   * **The board is drawn behind the whole page**, edge to edge, so "centred"
   * and "visible" are not the same thing wherever chrome is laid over it: at
   * 1280 the board spanned 176..1104 with a panel starting at 811, and the last
   * two files were simply behind it. `setViewInset` shifts the projection by
   * the covered margins, which is why this is the camera's business and not a
   * CSS one — the squares have to *project* into the free area or nothing can
   * be clicked there.
   *
   * **One call, in one place.** A second `setViewInset` anywhere else is a
   * second answer to where the free area is, and the last one to fire wins.
   *
   * `insets` is a sound dependency: `InsetStore` republishes a new snapshot
   * object only when a rect has actually moved, so this fires on a real change
   * and not once a frame.
   */
  useEffect(() => {
    board.current?.setViewInset(insets);
  }, [board, insets]);

  /**
   * The drivers' looks, honoured or refused here.
   *
   * One subscription for the life of the shell: the preferences and the board
   * are read through refs, so a checkbox moving does not resubscribe.
   */
  useEffect(
    () =>
      game.on("focus", (focus) => {
        if (focus.kind === "return") {
          // Nothing was borrowed, so there is nothing to give back — and
          // easing to the anchor would undo a pan the player made meanwhile.
          if (standardRef.current || !cameraRef.current.autoFocus) return;
          board.current?.returnView();
          return;
        }
        if (focus.kind !== "look") return;
        if (standardRef.current) return;
        const { autoFocus, trackPiece } = cameraRef.current;
        if (!autoFocus) return;
        const { squares, why } = focus;
        // A move is the look that fires every turn; an event is a front
        // landing. Switching the first off leaves the second, which is the
        // whole point of there being two toggles rather than one.
        if (why === "move" && !trackPiece) return;
        // **And only when it is not already in front of you.** A piece moving
        // in the middle of the viewport is one you are already watching, so
        // recentring on it moves the board out from under whatever you were
        // reading, every turn. Events are exempt: a front landing or ground
        // opening is the rare, large case the lead was built for.
        if (why === "move" && board.current?.isOnScreen(squares)) return;
        // New ground pulls the camera back to take in the whole board;
        // everything else slides across to it.
        board.current?.lookAt(squares, why === "region");
      }),
    [game, board]
  );

  /**
   * What the board is managing, for the driver's beat.
   *
   * A callback: the renderer is created in an effect, so anything read at
   * render time is read before it exists. `1` with no board is the honest
   * answer — nothing is being drawn, so nothing is behind.
   */
  const clockHealth = useCallback(() => board.current?.clockHealth() ?? 1, [board]);

  return { clockHealth, standardBoard };
}
